/**
 * Dev-panel error log. A bounded ring buffer that captures:
 *
 *   - explicit pushes via `pushDevError(...)` (use these from ix runners,
 *     fetch helpers, etc.)
 *   - any `console.error(...)` call once `installErrorCapture()` runs
 *   - unhandled promise rejections and `window.onerror` events
 *
 * Each entry is shaped so the user can copy a single block of text into a
 * bug report. Living under `client/src/dev/store/` because it should NEVER
 * be referenced from production rendering paths — the only consumers are the
 * dev console and the `installErrorCapture` hook (wired in `main.tsx`).
 */
import { create } from "zustand";

export interface DevErrorEntry {
  id: string;
  ts: number;
  source: string;
  message: string;
  stack?: string;
  /** Pre-formatted copy block: `[hh:mm:ss] source: message\nstack…`. */
  copyText: string;
}

interface DevErrorStore {
  entries: DevErrorEntry[];
  push: (e: Omit<DevErrorEntry, "id" | "ts" | "copyText">) => void;
  clear: () => void;
}

const MAX_ENTRIES = 200;
let counter = 0;

function makeId(): string {
  counter = (counter + 1) >>> 0;
  return `e${Date.now().toString(36)}-${counter.toString(36)}`;
}

function formatCopy(ts: number, source: string, message: string, stack?: string): string {
  const d = new Date(ts);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  const ss = d.getSeconds().toString().padStart(2, "0");
  const head = `[${hh}:${mm}:${ss}] ${source}: ${message}`;
  return stack ? `${head}\n${stack}` : head;
}

export const useDevErrorLog = create<DevErrorStore>((set) => ({
  entries: [],
  push: (e) =>
    set((s) => {
      const ts = Date.now();
      const entry: DevErrorEntry = {
        id: makeId(),
        ts,
        source: e.source,
        message: e.message,
        stack: e.stack,
        copyText: formatCopy(ts, e.source, e.message, e.stack),
      };
      const next = [...s.entries, entry];
      if (next.length > MAX_ENTRIES) next.splice(0, next.length - MAX_ENTRIES);
      return { entries: next };
    }),
  clear: () => set({ entries: [] }),
}));

/** Convenience push for callers that hold an `Error` (or anything `String`-able). */
export function pushDevError(source: string, err: unknown): void {
  let message: string;
  let stack: string | undefined;
  if (err instanceof Error) {
    message = err.message || err.name || "(empty Error)";
    stack = err.stack;
  } else if (typeof err === "string") {
    message = err;
  } else {
    try {
      message = JSON.stringify(err);
    } catch {
      message = String(err);
    }
  }
  useDevErrorLog.getState().push({ source, message, stack });
}

/**
 * Install global error capture. Idempotent — calling more than once is a
 * no-op. Patches `console.error`, attaches `window.onerror`, and listens
 * for `unhandledrejection`. Skips in non-DEV builds unless `?debug=1`.
 */
let installed = false;
export function installErrorCapture(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  const debugFlag = new URLSearchParams(window.location.search).get("debug") === "1";
  if (!isDev && !debugFlag) return;
  installed = true;

  const origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const message = args
        .map((a) => (a instanceof Error ? a.message : typeof a === "string" ? a : safeStringify(a)))
        .join(" ");
      const stack = (args.find((a) => a instanceof Error) as Error | undefined)?.stack;
      useDevErrorLog.getState().push({ source: "console.error", message, stack });
    } catch {
      /* never let our logger break the host console */
    }
    origConsoleError(...args);
  };

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    pushDevError("unhandledrejection", reason);
  });

  window.addEventListener("error", (ev) => {
    const message = ev.message || (ev.error instanceof Error ? ev.error.message : "(window error)");
    const stack = ev.error instanceof Error ? ev.error.stack : undefined;
    useDevErrorLog.getState().push({ source: "window.error", message, stack });
  });
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
